import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Layout from "@/components/Layout";
import Section from "@/components/Section";
import HeroBackground from "@/components/HeroBackground";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import Eyebrow from "@/components/Eyebrow";
import FAQAccordion from "@/components/FAQAccordion";
import IndustryCard from "@/components/IndustryCard";
import ProcessStep from "@/components/ProcessStep";
import ProofBar from "@/components/ProofBar";
import ServiceFlow from "@/components/ServiceFlow";
import SEO from "@/components/SEO";
import {
  SITE_URL,
  getCtaLabel,
  localize,
  siteContent,
} from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";
import { faqPageJsonLd, webPageJsonLd } from "@/seo/jsonLd";
import { usePageSeo } from "@/seo/usePageSeo";

const valueItems = [
  {
    code: "V/01",
    title: {
      en: "Reduce manual throughput bottlenecks",
      fr: "Réduire les goulots de débit manuel",
    },
    description: {
      en: "Classify, route, draft, reconcile, and escalate work with review boundaries where judgment or customer impact matters.",
      fr: "Classifier, router, rédiger, réconcilier et escalader avec des limites de revue là où le jugement ou l’impact client comptent.",
    },
    href: "/solutions/ai-agents-workflow-automation",
  },
  {
    code: "V/02",
    title: {
      en: "Turn messy documents into governed inputs",
      fr: "Transformer les documents complexes en entrees gouvernées",
    },
    description: {
      en: "Extract, validate, and review contracts, invoices, forms, support records, RFPs, and operational documents with traceable confidence and exception handling.",
      fr: "Extraire, valider et réviser contrats, factures, formulaires, dossiers support, appels d’offres et documents opérationnels avec confiance traçable et gestion des exceptions.",
    },
    href: "/solutions/document-intelligence",
  },
  {
    code: "V/03",
    title: {
      en: "Improve recurring commercial decisions",
      fr: "Améliorer les décisions commerciales récurrentes",
    },
    description: {
      en: "Forecast, score, prioritize, recommend, and route decisions where data exists but ownership, thresholds, and feedback still live in meetings.",
      fr: "Prévoir, scorer, prioriser, recommander et router les décisions quand les données existent mais que responsabilité, seuils et feedback vivent encore en réunion.",
    },
    href: "/solutions/predictive-models-decision-science",
  },
  {
    code: "V/04",
    title: {
      en: "Build the data layer AI can trust",
      fr: "Construire la couche de données que l’IA peut utiliser",
    },
    description: {
      en: "Create source-of-truth pipelines, semantic definitions, access rules, quality checks, and activation layers before automation scales bad inputs.",
      fr: "Creer pipelines source de vérité, définitions sémantiques, règles d’accès, contrôles qualité et couches d’activation avant d’automatiser de mauvais intrants.",
    },
    href: "/solutions/data-platforms-for-ai",
  },
];

const operatingSystemSteps = [
  { en: "Workflow selection", fr: "Sélection du flux" },
  { en: "Baseline and business case", fr: "Base de référence et cas d’affaires" },
  { en: "Data readiness", fr: "Préparation des données" },
  { en: "System design", fr: "Design du système" },
  { en: "Build and integration", fr: "Construction et intégration" },
  { en: "Evaluation and human review", fr: "Évaluation et revue humaine" },
  { en: "Monitoring and improvement", fr: "Monitoring et amélioration" },
  { en: "Expansion after trust is earned", fr: "Expansion après preuve de confiance" },
];

const processSteps = [
  {
    title: { en: "Fit call", fr: "Appel de cadrage" },
    description: {
      en: "We confirm the workflow, data reality, operating constraints, risk posture, and whether a production AI system is the right answer.",
      fr: "Nous confirmons le flux, la réalité des données, les contraintes opérationnelles, la posture de risque et si un système IA de production est la bonne réponse.",
    },
  },
  {
    title: { en: "Opportunity map", fr: "Carte d’opportunité" },
    description: {
      en: "We identify the highest-value workflow, the blockers, the owner, the business case, and the minimum system that can earn trust.",
      fr: "Nous identifions le flux à plus forte valeur, les blocages, le responsable, le cas d’affaires et le système minimum capable de gagner la confiance.",
    },
  },
  {
    title: { en: "Build and integrate", fr: "Construire et intégrer" },
    description: {
      en: "We design the data layer, workflow logic, AI behavior, review paths, integrations, and measurement loops as one system.",
      fr: "Nous concevons la couche de données, la logique de flux, le comportement IA, les revues, les intégrations et les boucles de mesure comme un seul système.",
    },
    marker: "HUMAN /",
  },
  {
    title: { en: "Operate and improve", fr: "Opérer et améliorer" },
    description: {
      en: "We ship into the workflow, monitor behavior, measure adoption and ROI, and expand only after users and controls prove the system can be trusted.",
      fr: "Nous livrons dans le flux, surveillons le comportement, mesurons l’adoption et le ROI, puis étendons seulement lorsque les utilisateurs et contrôles prouvent la confiance.",
    },
  },
];

const consoleMetrics = [
  { code: "M/01", en: "Workflow candidate", fr: "Flux candidat" },
  { code: "M/02", en: "Data readiness", fr: "Préparation données" },
  { code: "M/03", en: "Review boundary", fr: "Frontière de revue" },
  { code: "M/04", en: "ROI baseline", fr: "Base de ROI" },
];

const trustItems = [
  { en: "Evaluation harnesses", fr: "Harnais d’évaluation" },
  { en: "Prompt and workflow standards", fr: "Standards de prompts et de flux" },
  { en: "Human-in-the-loop controls", fr: "Contrôles avec revue humaine" },
  { en: "Escalation logic", fr: "Logique d’escalade" },
  { en: "Observability and monitoring", fr: "Observabilité et monitoring" },
  { en: "Data quality checks", fr: "Contrôles de qualité des données" },
  { en: "Security, privacy, and governance", fr: "Sécurité, confidentialité et gouvernance" },
  { en: "ROI and adoption measurement", fr: "Mesure du ROI et de l’adoption" },
];

const faqItems = [
  {
    q: "What does Vert AI actually build?",
    a: "Vert AI builds production AI systems, data platforms, workflow automations, and decision engines. The output is not a slide deck or demo. It is an adopted system designed to operate inside a real business workflow.",
  },
  {
    q: "How is this different from an AI automation agency?",
    a: "We start with workflow selection, data readiness, ownership, evaluation, monitoring, governance, and adoption. Automation is only useful when the workflow, controls, and measurement system are designed with production in mind.",
  },
  {
    q: "What makes an AI system production-grade?",
    a: "Production-grade means the system has reliable data inputs, clear ownership, evaluation criteria, human review where judgment matters, escalation paths, observability, security review, and measurable business outcomes.",
  },
  {
    q: "Where should a company start?",
    a: "Start with one expensive, recurring workflow where better speed, quality, routing, forecasting, or decision support would create measurable value. The first step is choosing the right workflow and mapping the data reality.",
  },
  {
    q: "Do we need perfect data before starting?",
    a: "No. But you do need to know where the data is trustworthy, where it is incomplete, and what quality checks are required before automation can be relied on.",
  },
  {
    q: "How do you measure ROI?",
    a: "We define the baseline before building, then track adoption, time saved, throughput, accuracy, cost reduction, decision quality, revenue lift, or margin impact depending on the workflow.",
  },
  {
    q: "What industries do you work with?",
    a: "Vert AI works with operators in retail and ecommerce, CPG, franchise and multi-location businesses, professional services, B2B services, and other operations-heavy companies.",
  },
  {
    q: "Can you work with our existing tools and data stack?",
    a: "Yes. The work usually starts by understanding your current systems, data stack, permissions, security constraints, workflows, and operating owners so the AI system fits the business instead of sitting beside it.",
  },
];

const frCopy = {
  heroEyebrow: "Systèmes IA, données et décision — du cadrage à la production",
  heroTitle: "Systèmes IA de production pour les opérateurs qui veulent des résultats, pas du théâtre IA.",
  heroSecondaryCta: "Faire le scorecard de préparation IA",
  problemHeadline: "La plupart des initiatives IA n’échouent pas parce que le modèle est faible.",
  problemBody:
    "Elles échouent parce que le flux de travail n’a jamais été redessiné, les données n’étaient pas prêtes, la responsabilité était floue, les utilisateurs ne faisaient pas confiance aux sorties, et personne n’avait défini comment mesurer le succès. Vert AI ferme cet écart avant que le code devienne un risque de production.",
  buildsHeadline: "Ce que Vert AI construit.",
  buildsBody:
    "Vert AI relié le cas d’usage, les données, la logique de flux, les contrôles, les responsables et la mesure business. Le résultat attendu est un système adopté, pas une démo isolée.",
  valueHeadline: "La valeur se voit dans le travail qui change.",
  valueBody:
    "Les bons cas d’usage sont opérationnels, mesurables et proches des décisions récurrentes. On choisit les flux où l’IA peut changer la vitesse, la qualité, le coût ou la précision sans retirer le jugement là où il compte.",
  osHeadline: "Du cas d’usage prometteur au système en production.",
  osBody:
    "Le travail avance par décisions courtes et observables : choisir le bon flux, cadrer la valeur, préparer les données, construire, évaluer, livrer et améliorer.",
  servicesHeadline: "Services conçus pour passer en production.",
  processHeadline: "Un chemin court du diagnostic au système exploité.",
  industriesHeadline: "Construit pour des opérateurs pour qui le flux de travail compte.",
  industriesBody:
    "Les meilleurs mandats se trouvent dans les entreprises où les données, les exceptions et les décisions récurrentes ont un coût réel.",
  trustHeadline: "L’IA de production exige plus qu’un modèle.",
  trustBody:
    "Le système doit être observable, gouverné, mesurable et utilisable par les équipes qui en dépendent. Les humains gardent le contrôle lorsque le jugement, le risque ou l’impact client comptent.",
  founderHeadline: "Expérience de production dirigée par le fondateur.",
  founderBody:
    "Dirigé par Guillaume, avec cadrage fondateur et expérience dans la conception et la livraison de systèmes IA, ML et données en production pour des opérateurs incluant Louis Vuitton, Canada Goose, Lowe’s et Home Hardware.",
  finalHeadline: "Savoir si votre meilleur cas d’usage mérite un système de production.",
  finalBody:
    "Apportez un flux de travail, un problème de données ou une décision récurrente. Nous cadrerons la valeur, le risque, la responsabilité et le chemin vers la production, incluant si le bon choix est de ne pas construire encore.",
};

const Index = () => {
  const { locale } = useTranslation();
  const isFr = locale === "fr";
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale);
  const secondaryCtaLabel = getCtaLabel(siteContent.ctas.secondary, locale);
  const seo = usePageSeo("home");
  const services = siteContent.services;
  const industries = siteContent.industries.slice(0, 5);
  const heroSecondaryCtaLabel = secondaryCtaLabel;
  const heroSupportBullets = isFr
    ? [
        "Choisir le bon flux de travail avant de construire.",
        "Ne pas automatiser avant que le risque et la responsabilité soient clairs.",
        "Corriger la fondation de données requise pour une IA utile.",
        "Livrer avec évaluation, monitoring, revue sécurité et revue humaine.",
        "Mesurer l’adoption, le ROI et l’impact opérationnel.",
      ]
    : [
        "Choose the right workflow before building.",
        "Do not automate before risk and ownership are clear.",
        "Fix the data foundation required for useful AI.",
        "Ship with evaluation, monitoring, security review, and human review.",
        "Measure adoption, ROI, and operating impact.",
      ];
  const heroMobileSubhead = isFr
    ? "Vert AI relie les flux réels, les données et les décisions récurrentes à des systèmes avec revue, monitoring et valeur mesurable."
    : "Vert AI turns real workflows, messy data, and recurring decisions into production systems with review paths, monitoring, and measurable value.";

  const heroFlowNodes = isFr
    ? [
        { label: "Flux", sub: "Choisir" },
        { label: "Données", sub: "Préparer" },
        { label: "Système", sub: "Construire" },
        { label: "Impact", sub: "Mesurer", emphasis: true },
      ]
    : [
        { label: "Workflow", sub: "Select" },
        { label: "Data", sub: "Ready" },
        { label: "System", sub: "Build" },
        { label: "Impact", sub: "Measure", emphasis: true },
      ];

  const proofItems = [
    {
      code: "01",
      label: isFr ? "Positionnement" : "Positioning",
      value: localize(siteContent.brand.shortPositioning, locale),
    },
    {
      code: "02",
      label: isFr ? "Livraison" : "Delivery",
      value: localize(siteContent.proof.founderLedDelivery, locale),
    },
    {
      code: "03",
      label: isFr ? "Expérience" : "Experience",
      value: localize(siteContent.proof.productionExperience, locale),
    },
    {
      code: "04",
      label: isFr ? "Contrôle" : "Controls",
      value: isFr
        ? "Évaluation, monitoring, revue humaine, gouvernance et mesure du ROI."
        : "Evaluation, monitoring, human review, governance, and ROI measurement.",
    },
  ];

  const problemFailures = isFr
    ? [
        {
          code: "01",
          label: "Flux",
          body: "Le flux de travail n’a jamais été redessiné autour de la décision, de l’exception et de la revue.",
        },
        {
          code: "02",
          label: "Données",
          body: "Les sources ne sont pas assez fiables, reliées ou contrôlées pour soutenir une automatisation utile.",
        },
        {
          code: "03",
          label: "Responsabilité",
          body: "Les propriétaires, les seuils de revue et les chemins d’escalade restent flous.",
        },
        {
          code: "04",
          label: "Adoption",
          body: "Les utilisateurs ne font pas confiance aux sorties parce que les critères de qualité ne sont pas visibles.",
        },
        {
          code: "05",
          label: "Mesure",
          body: "Le succès n’est pas défini avant la construction, donc le ROI devient une opinion après coup.",
        },
      ]
    : [
        {
          code: "01",
          label: "Workflow",
          body: "The workflow was never redesigned around the decision, exception, and review path.",
        },
        {
          code: "02",
          label: "Data",
          body: "The inputs are not reliable, connectéd, or governed enough for useful automation.",
        },
        {
          code: "03",
          label: "Ownership",
          body: "Owners, review thresholds, and escalation paths stay ambiguous.",
        },
        {
          code: "04",
          label: "Adoption",
          body: "Users do not trust the output because quality criteria are not visible.",
        },
        {
          code: "05",
          label: "Measurement",
          body: "Success is not defined before the build, so ROI becomes a post-hoc opinion.",
        },
      ];

  const methodChecks = isFr
    ? ["Flux mesurable", "Données utilisables", "Revue humaine définie", "Base de ROI"]
    : ["Measurable workflow", "Usable data", "Human review boundary", "ROI baseline"];

  const pageJsonLd = webPageJsonLd({
    url: SITE_URL,
    name: seo.title,
    description: seo.description,
  });
  const faqJsonLd = faqPageJsonLd(faqItems);

  return (
    <Layout>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={SITE_URL}
        keywords={seo.keywords}
        jsonLd={[pageJsonLd, faqJsonLd] as unknown as Record<string, unknown>[]}
      />

      <section className="homepage-hero page-hero-surface relative flex min-h-[92svh] items-start overflow-hidden grain md:min-h-[100svh] md:items-center lg:min-h-[92svh]">
        <HeroBackground quiet />
        <div className="absolute bottom-0 left-0 top-0 w-[2px] bg-primary/55" />
        <div className="container-wide relative z-10 w-full pb-12 pt-24 sm:pb-14 sm:pt-28 md:pb-20 md:pt-32 lg:pb-20 lg:pt-28">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[8fr_4fr] lg:gap-14">
            <div className="min-w-0 max-w-[52rem]">
              <div className="mb-5 opacity-0 animate-fade-up stagger-1 sm:mb-7">
                <Eyebrow
                  label={isFr ? frCopy.heroEyebrow : "AI, data, and decision systems — from scope to production"}
                  code="VERT / HOMEPAGE"
                  dark
                />
              </div>
              <div className="control-rule mb-6 w-20 opacity-0 animate-fade-up stagger-1 sm:mb-8 sm:w-24" />
              <h1 className="homepage-hero-title heading-hero mb-6 max-w-3xl text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-2 sm:mb-8 lg:!text-[4.35rem] xl:!text-[4.6rem]">
                {isFr ? (
                  <>
                    <span className="block sm:inline">Systèmes IA</span>{" "}
                    <span className="block sm:inline">de production</span>{" "}
                    <span className="block sm:inline">pour les opérateurs</span>{" "}
                    <span className="block sm:inline">qui veulent des résultats.</span>
                  </>
                ) : (
                  <>
                    <span className="block sm:inline">Production AI</span>{" "}
                    <span className="block sm:inline">systems for operators</span>{" "}
                    <span className="block sm:inline">who need results.</span>
                  </>
                )}
              </h1>
              <p className="mb-7 max-w-2xl text-[15.5px] leading-[1.66] text-[hsl(var(--sd-fg)/0.74)] opacity-0 animate-fade-up stagger-3 sm:hidden">
                {heroMobileSubhead}
              </p>
              <p className="text-lead mb-7 hidden max-w-2xl text-[hsl(var(--sd-fg)/0.72)] opacity-0 animate-fade-up stagger-3 sm:block">
                {localize(siteContent.brand.longPositioning, locale)}
              </p>
              <div className="flex flex-col gap-3 opacity-0 animate-fade-up stagger-4 sm:flex-row">
                <PrimaryCtaLink
                  className="btn-primary btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.secondary}
                  className="btn-outline btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {heroSecondaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
              <ul className="mt-6 hidden max-w-2xl grid-cols-2 gap-x-6 gap-y-3 border-t border-[hsl(var(--sd-fg)/0.12)] pt-5 opacity-0 animate-fade-up stagger-5 md:grid">
                {heroSupportBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-[hsl(var(--sd-fg)/0.68)]">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/75" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto hidden w-full max-w-md text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-5 lg:block lg:max-w-none">
              <div className="command-shell-dark p-5 md:p-6">
                <div className="relative z-10">
                  <div className="flex items-center justify-between gap-4 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                      {isFr ? "Système opérationnel" : "Production operating system"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.42)]">
                      READY / 08
                    </span>
                  </div>

                  <ServiceFlow nodes={heroFlowNodes} stamp={isFr ? "SYSTÈME / 01" : "SYSTEM / 01"} />

                  <div className="mt-5 border-y border-[hsl(var(--sd-fg)/0.09)]">
                    {(isFr
                      ? [
                          ["Signal", "Cas d’usage mesurable"],
                          ["Contrôle", "Revue humaine définie"],
                          ["Décision", "Chemin de production clair"],
                        ]
                      : [
                          ["Signal", "Measurable use case"],
                          ["Control", "Human review boundary"],
                          ["Decision", "Clear production path"],
                        ]
                    ).map(([label, value]) => (
                      <div key={label} className="status-row first:border-t-0">
                        <span className="status-dot" aria-hidden="true" />
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.44)]">
                          {label}
                        </span>
                        <span className="text-right text-[12.5px] leading-snug text-[hsl(var(--sd-fg)/0.72)]">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.09)] bg-[hsl(var(--sd-fg)/0.09)]">
                    {consoleMetrics.map((item) => (
                      <div key={item.code} className="bg-[hsl(var(--control-panel))] px-3 py-3">
                        <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-primary/68">
                          {item.code}
                        </span>
                        <span className="mt-1 block text-[12px] text-[hsl(var(--sd-fg)/0.68)]">
                          {isFr ? item.fr : item.en}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProofBar items={proofItems} className="dark:hidden" />
      <ProofBar items={proofItems} dark className="hidden dark:block" />

      <Section>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
          <div className="min-w-0">
            <Eyebrow label={isFr ? "Le problème" : "The problem"} code="P / 01" className="mb-6" />
            <h2 className="heading-section max-w-2xl text-foreground">
              {isFr ? frCopy.problemHeadline : "Most AI initiatives do not fail because the model is weak."}
            </h2>
          </div>
          <div className="min-w-0 border-t border-border/35 pt-8 lg:pt-10">
            <p className="max-w-2xl text-[16px] leading-[1.8] text-muted-foreground">
              {isFr
                ? "Elles échouent pour des raisons de production : flux, données, responsabilité, confiance et mesure. Vert AI ferme cet écart avant que le code devienne un risque de production."
                : "They fail for production reasons: workflow, data, ownership, trust, and measurement. Vert AI closes that gap before code becomes production risk."}
            </p>
            <ul className="mt-8 divide-y divide-border/45 border-y border-border/45">
              {problemFailures.map((item) => (
                <li
                  key={item.code}
                  className="grid grid-cols-[3.25rem_1fr] items-start gap-x-4 gap-y-2 py-5 sm:grid-cols-[4.75rem_0.38fr_1fr] sm:gap-x-6 sm:gap-y-0"
                >
                  <span className="pt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                    P/{item.code}
                  </span>
                  <h3 className="text-[15px] font-semibold leading-snug text-foreground">{item.label}</h3>
                  <p className="col-start-2 text-[14px] leading-[1.72] text-muted-foreground sm:col-start-3 sm:max-w-xl">
                    {item.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Ce que Vert AI construit" : "What Vert AI builds"} code="B / 05" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {isFr ? frCopy.buildsHeadline : "What Vert AI builds."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.68)]">
                {isFr
                  ? frCopy.buildsBody
                  : "Vert AI ties the use case, data, workflow logic, controls, and business measurement into one production path. The intended output is an operating system, not an isolated demo."}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <Link
                  to="/solutions"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_builds"
                  data-event-surface="builds_section"
                  data-event-target="/solutions"
                >
                  {isFr ? "Explorer les systèmes IA de production" : "Explore production AI systems"}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="mt-10 border-y border-[hsl(var(--sd-fg)/0.1)] py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                  {isFr ? "Signal d’achat" : "Buyer signal"}
                </p>
                <p className="mt-3 max-w-md text-[14px] leading-[1.72] text-[hsl(var(--sd-fg)/0.62)]">
                  {isFr
                    ? "Le bon point de départ est un flux récurrent où une meilleure vitesse, qualité, priorisation ou revue peut être mesurée. Tous les flux ne devraient pas être automatisés."
                    : "The right starting point is a recurring workflow where better speed, quality, prioritization, or review can be measured. Not every workflow should be automated."}
                </p>
              </div>
            </div>

            <div className="min-w-0">
              <div className="system-card-dark p-5 md:p-6">
                <div className="flex flex-col gap-2 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4 sm:flex-row sm:items-end sm:justify-between sm:pb-5">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                      {isFr ? "Index de capacités" : "Capability index"}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {isFr ? frCopy.servicesHeadline : "Services built to reach production."}
                    </h3>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.5)]">
                    S / {String(services.length).padStart(2, "0")}
                  </span>
                </div>
                <div className="divide-y divide-[hsl(var(--sd-fg)/0.08)]">
                  {services.map((service, index) => (
                    <Link
                      key={service.slug}
                      to={service.href}
                      className="group grid gap-2 py-3.5 transition-colors duration-400 sm:grid-cols-[4.25rem_1fr_auto] sm:items-start sm:gap-3 sm:py-4"
                      data-event="service_page_cta_click"
                      data-event-surface="homepage_capability_index"
                      data-event-target={service.href}
                      data-event-service-slug={service.slug}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                        S/{String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.88)] transition-colors duration-400 group-hover:text-primary">
                          {localize(service.name, locale)}
                        </span>
                        <span className="mt-1.5 block max-w-2xl text-[13.5px] leading-[1.62] text-[hsl(var(--sd-fg)/0.58)] sm:mt-2 sm:leading-[1.68]">
                          {localize(service.description, locale)}
                        </span>
                      </span>
                      <ArrowRight className="mt-1 h-3.5 w-3.5 text-[hsl(var(--sd-fg)/0.42)] transition-all duration-400 group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                      {isFr ? "Modèles de valeur" : "Value patterns"}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {isFr ? frCopy.valueHeadline : "Value shows up when the workflow changes."}
                    </h3>
                  </div>
                  <p className="max-w-sm text-[13.5px] leading-[1.65] text-[hsl(var(--sd-fg)/0.58)]">
                    {isFr
                      ? frCopy.valueBody
                      : "The best use cases are operational, measurable, and close to recurring decisions. Humans stay in control where judgment, risk, or customer impact matters."}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {valueItems.map((item) => (
                    <Link
                      key={item.code}
                      to={item.href}
                      className="system-card-dark group border-l-2 border-l-transparent p-4 transition-colors duration-400 hover:border-l-primary/60 hover:border-primary/35 sm:p-5"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">{item.code}</span>
                      <h3 className="mt-4 text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.9)] transition-colors duration-400 group-hover:text-primary">
                        {localize(item.title, locale)}
                      </h3>
                      <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[hsl(var(--sd-fg)/0.58)] sm:mt-3 sm:leading-[1.7]">
                        {localize(item.description, locale)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.74fr_1.26fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Du cadrage à la production" : "Scope to production"} code="OS / 08" className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-foreground">
                {isFr ? frCopy.osHeadline : "From promising use case to production system."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-muted-foreground">
                {isFr
                  ? frCopy.osBody
                  : "The work advances through short, observable decisions: select the workflow, define value, ready the data, build, evaluate, ship, and improve."}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_midpage"
                  dataEventSurface="scope_to_production_section"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <Link
                  to="/services"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_midpage"
                  data-event-surface="scope_to_production_section"
                  data-event-target="/services"
                >
                  {isFr ? "Explorer les systèmes IA de production" : "Explore production AI systems"}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="mt-10 border-t border-border/40 pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                  {isFr ? "Garde-fous de production" : "Production gates"}
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  {methodChecks.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-muted-foreground">
                      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/75" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/60 xl:grid-cols-4">
                {operatingSystemSteps.map((step, index) => (
                  <div key={localize(step, locale)} className="bg-surface-1 p-4 sm:p-5 md:p-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                      OS/{String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-4 text-[14px] font-semibold leading-snug text-foreground sm:mt-5 sm:text-[15px]">
                      {localize(step, locale)}
                    </h3>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-1 gap-7 lg:mt-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-10">
                <div className="system-card p-5 sm:p-6 md:p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                    {isFr ? "Méthode" : "Delivery method"}
                  </p>
                  <h3 className="mt-5 text-[18px] font-semibold leading-snug text-foreground">
                    {isFr ? frCopy.processHeadline : "A short path from diagnostic to operating system."}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[1.75] text-muted-foreground">
                    {isFr
                      ? "Le travail commence par le problème opérationnel, pas par le modèle. Chaque étape réduit le risque avant la production."
                      : "The work starts with the operating problem, not the model. Each step reduces delivery risk before production."}
                  </p>
                </div>
                <ul className="border-t border-border/35">
                  {processSteps.map((step, index) => (
                    <ProcessStep
                      key={localize(step.title, locale)}
                      code={`P/${String(index + 1).padStart(2, "0")}`}
                      title={localize(step.title, locale)}
                      description={localize(step.description, locale)}
                      marker={step.marker}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Confiance" : "Trust and governance"} code="T / 08" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {isFr ? frCopy.trustHeadline : "Production AI needs more than a model."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.68)]">
                {isFr
                  ? frCopy.trustBody
                  : "The system has to be observable, governed, measurable, and usable by the teams who depend on it. Humans stay in control where judgment, risk, or customer impact matters."}
              </p>
              <PrimaryCtaLink
                cta={siteContent.ctas.secondary}
                className="btn-outline group mt-8 w-full justify-center sm:w-auto"
                dataEventLocation="homepage_trust"
                dataEventSurface="trust_section"
              >
                {secondaryCtaLabel}
                <ArrowRight className="h-3 w-3" />
              </PrimaryCtaLink>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--sd-fg)/0.1)]">
                {trustItems.map((item, index) => (
                  <div key={localize(item, locale)} className="bg-[hsl(var(--control-panel))] p-4 sm:p-5 md:p-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/72">
                      T/{String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="mt-3 text-[13.5px] leading-[1.62] text-[hsl(var(--sd-fg)/0.74)] sm:mt-4 sm:text-[14px] sm:leading-[1.68]">
                      {localize(item, locale)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="system-card-dark mt-8 p-6 md:p-7">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.34fr_0.66fr]">
                  <div>
                    <Eyebrow label={isFr ? "Fondateur" : "Founder credibility"} code="F / 01" dark className="mb-6" />
                    <div className="control-rule mb-6 w-20" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.58)]">
                      Guillaume Belisle / Vert AI
                    </p>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[22px] font-semibold leading-tight text-[hsl(var(--sd-fg))] md:text-[26px]">
                      {isFr ? frCopy.founderHeadline : "Founder-led production experience."}
                    </h3>
                    <p className="mt-5 max-w-2xl text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.66)]">
                      {isFr
                        ? frCopy.founderBody
                        : "Founder-led by Guillaume, with scoping and senior technical involvement informed by experience designing and shipping production AI, ML, and data systems for operators including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware."}
                    </p>
                    <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                      <Link
                        to="/about"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="secondary_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/about"
                      >
                        {isFr ? "Voir la livraison dirigée par le fondateur" : "Review founder-led delivery"}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                      <Link
                        to="/insights"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="insights_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/insights"
                      >
                        {isFr ? "Lire l’analyse d’implémentation" : "Read implementation analysis"}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="mb-10 grid grid-cols-1 gap-8 lg:grid-cols-[0.8fr_1fr] lg:items-end lg:gap-16">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Opérateurs" : "Who it is for"} code="I / 05" className="mb-6" />
              <h2 className="heading-section mb-5 max-w-2xl text-foreground">
                {isFr ? frCopy.industriesHeadline : "Built for operators where workflow quality matters."}
              </h2>
              <p className="max-w-2xl text-[15px] leading-[1.8] text-muted-foreground">
                {isFr
                  ? frCopy.industriesBody
                  : "The best engagements sit inside companies where data quality, exceptions, recurring decisions, and operational throughput have real cost."}
              </p>
            </div>
            <div className="flex lg:justify-end">
              <Link
                to="/industries"
                className="btn-outline w-full justify-center sm:w-auto"
                data-event="secondary_cta_click"
                data-event-location="homepage_industries"
                data-event-surface="industries_section"
                data-event-target="/industries"
              >
                {isFr ? "Voir l’adéquation par industrie" : "Review industry fit"}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {industries.map((industry, index) => (
              <li key={industry.slug}>
                <IndustryCard
                  code={`I/${String(index + 1).padStart(2, "0")}`}
                  title={localize(industry.label, locale)}
                  description={
                    isFr
                      ? "Flux opérationnels, données et décisions récurrentes où un système IA doit être fiable."
                      : "Operational workflows, data, and recurring decisions where an AI system needs to be trusted."
                  }
                  href={industry.href}
                  ctaLabel={isFr ? "Voir l’adéquation" : "Review industry fit"}
                />
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section narrow padding="sm">
        <Eyebrow label="FAQ" code={`FAQ / ${String(faqItems.length).padStart(2, "0")}`} className="mb-6" />
        <h2 className="heading-section mb-8 text-foreground sm:mb-12">
          {isFr ? "Questions fréquentes." : "Direct answers for serious buyers."}
        </h2>
        <FAQAccordion items={faqItems} />
      </Section>

      <section className="relative overflow-hidden section-dark grain">
        <div className="shimmer-line" />
        <div className="container-wide section-padding relative z-10">
          <div className="control-panel mx-auto max-w-4xl p-7 sm:p-10 md:p-14">
            <Eyebrow label={isFr ? "Prochaine décision" : "Next decision"} code="CTA / FINAL" dark className="mb-10" />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-end">
              <div className="min-w-0">
                <h2 className="heading-editorial mb-6 max-w-2xl text-[hsl(var(--sd-fg))]">
                  {isFr ? frCopy.finalHeadline : "Find out whether your best AI use case deserves a production system."}
                </h2>
                <p className="max-w-xl text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.66)]">
                  {isFr
                    ? frCopy.finalBody
                    : "Bring a workflow, a data problem, or a recurring decision. We will map the value, risks, ownership, and path to production, including whether not to build yet."}
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.secondary}
                  className="btn-outline group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {secondaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
